/**
 * src/lib/forms/dynamicRenderer.tsx
 *
 * F7 — Renderer dinámico de cuestionarios JSON Schema-driven.
 *
 * Lee una FormDefinition (servida por GET /api/forms/:code) y produce inputs React
 * según el tipo (choice/boolean/text/integer/decimal/date/display). Recolecta las
 * respuestas en un Record<linkId, answer[]> compatible con el endpoint
 * POST /api/questionnaire-responses.
 *
 * Mobile-first (Tailwind v4 — sin clases custom). No depende de shadcn/ui — usamos
 * inputs nativos con estilos Tailwind para minimizar peso bundle.
 *
 * Autosave: el componente emite onChange(items) cuando cambia cualquier campo. El
 * caller decide si dispara PATCH (autosave) y/o submit final.
 */
import { useEffect, useMemo, useRef, useState } from 'react'

export interface FormDefinitionItem {
  linkId: string
  text?: string
  subtext?: string
  type: 'choice' | 'boolean' | 'text' | 'integer' | 'decimal' | 'date' | 'display'
  required?: boolean
  max_length?: number
  min?: number
  max?: number
  placeholder?: string
  options_ref?: string
  options?: Array<{ value: string | number; label: string }>
  conditional?: {
    enableWhenLinkId: string
    enableWhenAnswerBoolean?: boolean
    enableWhenAnswerValue?: string | number | boolean
  }
}

export interface FormDefinition {
  code: string
  version: string
  title: string
  subtitle?: string
  description?: string
  audience: 'worker' | 'staff'
  citation?: string
  options_likert_0_3?: Array<{ value: number; label: string }>
  items: FormDefinitionItem[]
  scoring?: unknown
}

export type AnswerValue = {
  valueInteger?: number
  valueBoolean?: boolean
  valueString?: string
  valueDate?: string
}

export interface QuestionnaireItem {
  linkId: string
  answer: AnswerValue[]
}

export interface DynamicRendererProps {
  definition: FormDefinition
  initialItems?: QuestionnaireItem[]
  onChange?: (items: QuestionnaireItem[]) => void
  onBlur?: (items: QuestionnaireItem[]) => void
  disabled?: boolean
}

/**
 * Build local state map from items array.
 */
function buildAnswerMap(items: QuestionnaireItem[]): Map<string, AnswerValue> {
  const map = new Map<string, AnswerValue>()
  for (const it of items) {
    if (it.answer[0]) map.set(it.linkId, it.answer[0])
  }
  return map
}

function mapToItems(map: Map<string, AnswerValue>): QuestionnaireItem[] {
  const items: QuestionnaireItem[] = []
  for (const [linkId, answer] of map.entries()) {
    items.push({ linkId, answer: [answer] })
  }
  return items
}

function resolveOptions(
  item: FormDefinitionItem,
  def: FormDefinition,
): Array<{ value: string | number; label: string }> {
  if (item.options) return item.options
  if (item.options_ref === 'options_likert_0_3' && def.options_likert_0_3) {
    return def.options_likert_0_3
  }
  return []
}

function shouldRenderItem(item: FormDefinitionItem, answers: Map<string, AnswerValue>): boolean {
  if (!item.conditional) return true
  const target = answers.get(item.conditional.enableWhenLinkId)
  if (!target) return false
  if (item.conditional.enableWhenAnswerBoolean !== undefined) {
    return target.valueBoolean === item.conditional.enableWhenAnswerBoolean
  }
  if (item.conditional.enableWhenAnswerValue !== undefined) {
    const v = item.conditional.enableWhenAnswerValue
    return target.valueString === v || target.valueInteger === v || target.valueBoolean === v
  }
  return true
}

export function DynamicForm({
  definition,
  initialItems = [],
  onChange,
  onBlur,
  disabled,
}: DynamicRendererProps) {
  const [answers, setAnswers] = useState<Map<string, AnswerValue>>(() =>
    buildAnswerMap(initialItems),
  )

  const totalRequired = useMemo(
    () => definition.items.filter((i) => i.required && i.type !== 'display').length,
    [definition.items],
  )
  const answeredRequired = useMemo(() => {
    let n = 0
    for (const it of definition.items) {
      if (!it.required || it.type === 'display') continue
      if (!shouldRenderItem(it, answers)) continue
      const a = answers.get(it.linkId)
      if (
        a &&
        (a.valueInteger !== undefined ||
          a.valueBoolean !== undefined ||
          (a.valueString !== undefined && a.valueString.length > 0) ||
          a.valueDate !== undefined)
      ) {
        n++
      }
    }
    return n
  }, [definition.items, answers])

  // #22: el updater de setAnswers DEBE ser puro. Antes llamaba onChange() (setState del padre)
  // dentro del updater → "Cannot update a component while rendering a different component".
  // Se emite onChange en un effect cuando `answers` cambia. Ref a onChange (handleChange del
  // padre NO está memoizado) para no incluirlo en deps y evitar un loop de renders.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return // no emitir onChange en el montaje (paridad con el comportamiento previo)
    }
    onChangeRef.current?.(mapToItems(answers))
  }, [answers])

  function update(linkId: string, partial: AnswerValue): void {
    setAnswers((prev) => {
      const next = new Map(prev)
      const existing = next.get(linkId) ?? {}
      next.set(linkId, { ...existing, ...partial })
      return next
    })
  }

  function handleBlur(): void {
    onBlur?.(mapToItems(answers))
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
      <header>
        <h1 className="text-2xl font-bold text-slate-900">{definition.title}</h1>
        {definition.subtitle && (
          <p className="mt-1 text-sm text-slate-600">{definition.subtitle}</p>
        )}
        {definition.description && (
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{definition.description}</p>
        )}
        <div className="mt-4 h-2 w-full rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-fitlab-gradient transition-all"
            style={{
              width: `${totalRequired === 0 ? 100 : (answeredRequired / totalRequired) * 100}%`,
            }}
          />
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {answeredRequired} de {totalRequired} requeridos
        </p>
      </header>

      <div className="flex flex-col gap-5">
        {definition.items.map((item) => {
          if (!shouldRenderItem(item, answers)) return null

          if (item.type === 'display') {
            return (
              <div
                key={item.linkId}
                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
              >
                {item.text && <div className="font-medium">{item.text}</div>}
                {item.subtext && <div className="mt-1 text-xs text-slate-500">{item.subtext}</div>}
              </div>
            )
          }

          const value = answers.get(item.linkId)

          if (item.type === 'choice') {
            const options = resolveOptions(item, definition)
            return (
              <fieldset key={item.linkId} className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-slate-900">
                  {item.text}
                  {item.required && <span className="text-red-500"> *</span>}
                </legend>
                <div className="flex flex-col gap-1.5">
                  {options.map((opt) => {
                    const isInteger = typeof opt.value === 'number'
                    const isSelected = isInteger
                      ? value?.valueInteger === opt.value
                      : value?.valueString === String(opt.value)
                    return (
                      <label
                        key={String(opt.value)}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
                          isSelected
                            ? 'border-[color:var(--color-fitlab-blue)] bg-[color:var(--color-fitlab-blue)]/5'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={item.linkId}
                          value={String(opt.value)}
                          checked={isSelected}
                          disabled={disabled}
                          className="h-4 w-4 accent-[color:var(--color-fitlab-blue)]"
                          onChange={() => {
                            if (isInteger)
                              update(item.linkId, { valueInteger: opt.value as number })
                            else update(item.linkId, { valueString: String(opt.value) })
                          }}
                          onBlur={handleBlur}
                        />
                        <span className="text-sm text-slate-800">{opt.label}</span>
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            )
          }

          if (item.type === 'boolean') {
            return (
              <fieldset key={item.linkId} className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-slate-900">
                  {item.text}
                  {item.required && <span className="text-red-500"> *</span>}
                </legend>
                <div className="flex gap-2">
                  {[
                    { label: 'Sí', value: true },
                    { label: 'No', value: false },
                  ].map((opt) => {
                    const isSelected = value?.valueBoolean === opt.value
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        disabled={disabled}
                        className={`flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors ${
                          isSelected
                            ? 'border-[color:var(--color-fitlab-blue)] bg-[color:var(--color-fitlab-blue)] text-white'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                        }`}
                        onClick={() => update(item.linkId, { valueBoolean: opt.value })}
                        onBlur={handleBlur}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </fieldset>
            )
          }

          if (item.type === 'text') {
            return (
              <label key={item.linkId} className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-900">
                  {item.text}
                  {item.required && <span className="text-red-500"> *</span>}
                </span>
                <textarea
                  rows={3}
                  maxLength={item.max_length}
                  disabled={disabled}
                  placeholder={item.placeholder}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-fitlab-blue)]/20"
                  value={value?.valueString ?? ''}
                  onChange={(e) => update(item.linkId, { valueString: e.target.value })}
                  onBlur={handleBlur}
                />
              </label>
            )
          }

          if (item.type === 'integer' || item.type === 'decimal') {
            return (
              <label key={item.linkId} className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-900">
                  {item.text}
                  {item.required && <span className="text-red-500"> *</span>}
                </span>
                <input
                  type="number"
                  min={item.min}
                  max={item.max}
                  step={item.type === 'integer' ? 1 : 'any'}
                  inputMode={item.type === 'integer' ? 'numeric' : 'decimal'}
                  disabled={disabled}
                  placeholder={item.placeholder}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-fitlab-blue)]/20"
                  value={value?.valueInteger ?? ''}
                  onChange={(e) => {
                    const n = e.target.value === '' ? undefined : Number(e.target.value)
                    if (n === undefined) update(item.linkId, { valueInteger: undefined })
                    else update(item.linkId, { valueInteger: n })
                  }}
                  onBlur={handleBlur}
                />
              </label>
            )
          }

          if (item.type === 'date') {
            return (
              <label key={item.linkId} className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-slate-900">
                  {item.text}
                  {item.required && <span className="text-red-500"> *</span>}
                </span>
                <input
                  type="date"
                  disabled={disabled}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-fitlab-blue)]/20"
                  value={value?.valueDate ?? ''}
                  onChange={(e) => update(item.linkId, { valueDate: e.target.value })}
                  onBlur={handleBlur}
                />
              </label>
            )
          }

          return null
        })}
      </div>

      {definition.citation && (
        <p className="text-xs italic text-slate-400">{definition.citation}</p>
      )}
    </form>
  )
}
